import { clients } from 'lib/https';
import { User } from '/types/user-type';
import { CreateUserFormType, ProfileFormType } from '../utils/utils';
import { ConfigurationSettings } from '/models/configuration-settings';
import { getOidcRuntimeEnv } from 'lib/runtime-env';

type GetAccountResponse = {
  data: User;
  errors: unknown;
};

export interface UserDetailsData {
  itemId: string;
  createdDate: string;
  lastUpdatedDate: string;
  language: string;
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  roles: string[];
  permissions: string[];
  active: boolean;
  status: number;
  isVerified: boolean;
  profileImageUrl: string;
  mfaEnabled: boolean;
  isMfaVerified: boolean;
  userMfaType: number;
  externalIdentities: any[];
  attributes: Record<string, any>;
  logInCount: number;
  lastLoggedInTime: string;
  lastLoggedInDeviceInfo: string;
  organizationIds: string[];
  OrganizationsRoles: Record<string, string[]>;
  OrganizationsPermissions: Record<string, string[]>;
}

interface GetUserByIdResponse {
  data: UserDetailsData;
  errors: unknown;
}

interface UpdateUserResponse {
  itemId: string;
  errors: unknown;
  isSuccess: boolean;
}

const buildGetAccountUrl = (config: ConfigurationSettings) => {
  const oidcEnv = getOidcRuntimeEnv(config.paths.baseUrl);
  /* if (oidcEnv.idpBaseUrl) {
    return `${oidcEnv.idpBaseUrl.replace(/\/$/, '')}/api/Iam/me`;
  } */

  const baseUrl = `https://${config.paths.baseUrl}`;
  return `${baseUrl}${config.paths.iam.getAccount}`;
};

const mapAccountResponseToUser = (userData: GetAccountResponse['data']): User => ({
  ...userData,
  roles: userData.memberships?.[0]?.roles ?? userData.roles ?? [],
});

export const getAccountWithAccessToken = async (
  config: ConfigurationSettings,
  accessToken: string
): Promise<User> => {
  const url = buildGetAccountUrl(config);
  const oidcEnv = getOidcRuntimeEnv(config.paths.baseUrl);
  const isCookieAuth = !!oidcEnv.idpBaseUrl;
  const res = await clients.get<GetAccountResponse>(
    url,
    config.blocksKey,
    !isCookieAuth && accessToken
      ? {
          Authorization: `bearer ${accessToken}`,
        }
      : undefined
  );

  return mapAccountResponseToUser(res.data);
};

/**
 * Fetches the account details of the currently authenticated user.
 *
 * @returns {Promise<User>} The user's account data.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const account = await getAccount();
 */

export const getAccount = async (config: ConfigurationSettings): Promise<User> => {
  const url = buildGetAccountUrl(config);
  const res = await clients.get<GetAccountResponse>(url, config.blocksKey);

  return mapAccountResponseToUser(res.data);
};

/**
 * Fetches user details by user ID.
 *
 * @param {string} userId - The ID of the user to fetch
 * @param {ConfigurationSettings} config - The configuration settings
 * @returns {Promise<UserDetailsData>} The user's details.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const userDetails = await getUserById(userId, config);
 */
export const getUserById = async (
  userId: string,
  config: ConfigurationSettings
): Promise<UserDetailsData> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.getUserById.replace(':userId', userId)}`;
  
  const res = await clients.get<GetUserByIdResponse>(url, config.blocksKey);
  return res.data;
};

/**
 * Updates the preferred language of a user.
 *
 * @example
 * await updateUserLanguage(userId, 'de-DE', config);
 */
export const updateUserLanguage = (
  userId: string,
  language: string,
  config: ConfigurationSettings
) => {
  return clients.post<UpdateUserResponse>(
    buildGetAccountUrl(config),
    config.blocksKey,
    JSON.stringify({ language })
  );
};

/**
 * Creates a new user account with the provided form data.
 *
 * @param {CreateUserFormType} data - The form data used to create the user account.
 *
 * @returns {Promise<{ itemId: string, errors: unknown, isSuccess: boolean }>}
 * The response containing the itemId, any errors, and the success status of the creation.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await createAccount(formData);
 */

export const createAccount = (data: CreateUserFormType, config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.createAccount}`;

  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(data));
};

/**
 * Updates the account profile of the currently authenticated user with the provided data.
 *
 * @param {ProfileFormType} data - The form data used to update the user's profile.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey
 * @param {string} userId - The ID of the user to update
 * @param {boolean} [isSelf] - True when the signed-in user is updating their own account.
 *   Own updates use POST Me. Other users stay on POST /iam/users/:userId.
 *
 * @returns {Promise<{ itemId: string, errors: unknown, isSuccess: boolean }>}
 * The response containing the itemId, any errors, and the success status of the update.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateAccount(profileData, config, userId, true);
 */
export const updateAccount = async (
  data: ProfileFormType,
  config: ConfigurationSettings,
  userId: string,
  isSelf = false
): Promise<UpdateUserResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const meUrl = buildGetAccountUrl(config);
  const userByIdUrl = `${baseUrl}${config.paths.iam.getUserById.replace(':userId', userId)}`;

  const userResponse = isSelf
    ? await clients.get<GetAccountResponse>(meUrl, config.blocksKey)
    : await clients.get<GetUserByIdResponse>(userByIdUrl, config.blocksKey);

  const { itemId, ...withoutItemId } = {
    ...userResponse.data,
    ...data,
  };

  return clients.post<UpdateUserResponse>(
    isSelf ? meUrl : userByIdUrl,
    config.blocksKey,
    JSON.stringify(isSelf ? withoutItemId : { ...withoutItemId, itemId })
  );
};

export type UpdateUserAccessPayload = {
  roles: string[];
  permissions: string[];
  organizationId: string;
  userId: string;
};

/**
 * Updates a user's roles/permissions for an organization (Blocks IAM access API).
 */
export const updateUserAccess = async (
  payload: UpdateUserAccessPayload,
  config: ConfigurationSettings
) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.updateUserAccess}`;

  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

/**
 * Changes the password for the currently authenticated user.
 *
 * @param {Object} params - The password change parameters.
 * @param {string} params.newPassword - The new password to set.
 * @param {string} params.oldPassword - The current password to authenticate the change.
 *
 * @returns {Promise<any>} The response from the server after attempting to change the password.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await changePassword({ oldPassword: 'oldPass', newPassword: 'newPass' });
 */
export const changePassword = async ({
  newPassword,
  oldPassword,
  config,
}: {
  newPassword: string;
  oldPassword: string;
  config: ConfigurationSettings;
}) => {
  const payload = {
    newPassword,
    oldPassword,
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.changePassword}`;

  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

/**
 * Deactivates the account of the currently authenticated user.
 * This action is irreversible and will permanently delete the user's account.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey
 * @param {string} userId - The ID of the user to deactivate
 * @returns {Promise<{ itemId: string; errors: any; isSuccess: boolean }>} A promise that resolves with the deactivation result
 * @throws {Error} If the request fails or the server returns an error
 *
 * @example
 * const result = await deactivateAccount(config, userId);
 */
export const deactivateAccount = async (
  config: ConfigurationSettings,
  userId: string,
  isDeleteForever?: boolean
): Promise<{ itemId: string; errors: any; isSuccess: boolean }> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = isDeleteForever ? `${baseUrl}${config.paths.iam.deleteUser}` : `${baseUrl}${config.paths.iam.disableUser}`;
  
  const payload = {
    userId,
    projectKey: config.blocksKey,
  };

  return clients.post<{ itemId: string; errors: any; isSuccess: boolean }>(
    url,
    config.blocksKey,
    JSON.stringify(payload)
  );
};
